import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import SiteFooter from "../components/SiteFooter";
import useBrand from "../hooks/useBrand";

export default function Login() {
  const { login } = useAuth();
  const { brandName } = useBrand();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirectParam = params.get("redirect");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const u = await login(email, password);
      if (redirectParam) navigate(redirectParam);
      else if (u.role === "super_admin" || u.role === "admin") navigate("/admin");
      else if (u.role === "staff" || u.role === "editor") navigate("/admin/bookings");
      else navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="h-screen max-h-[1080px] max-w-[1920px] mx-auto overflow-hidden flex flex-col justify-between"
      style={{ background: "#F6F5F2" }}
      data-testid="login-page"
    >
      {/* Minimal top nav — brand only, no scroll-triggering CTAs. */}
      <header className="px-6 md:px-12 lg:px-20 py-4 border-b border-black/5 flex items-center justify-between shrink-0">
        <Link to="/" className="font-display text-2xl tracking-tight" data-testid="login-brand">
          Car <span className="italic font-medium">Rental App</span>
          <span className="text-neutral-400 font-normal"> · {brandName}</span>
        </Link>
      </header>

      <div className="flex-1 w-full px-6 md:px-12 lg:px-20 flex items-center overflow-hidden">
      <div className="grid lg:grid-cols-12 gap-12 items-center max-w-6xl mx-auto">
        <div className="lg:col-span-6">
          <p className="text-xs font-medium tracking-wider text-neutral-500 uppercase mb-3">Welcome back</p>
          <h1 className="font-display text-5xl md:text-6xl tracking-tight">
            Sign <em className="italic">in</em>.
          </h1>
          <p className="mt-4 text-neutral-600 max-w-md">
            Access your bookings, manage reservations, and continue your journey with Car Rental App.
          </p>
          <form onSubmit={handleSubmit} className="mt-6 max-w-md space-y-4">
            <div>
              <label className="block text-xs font-medium tracking-wider text-neutral-500 uppercase mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="editorial-input w-full"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium tracking-wider text-neutral-500 uppercase mb-2">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="editorial-input w-full"
                data-testid="login-password-input"
              />
            </div>
            {error && <p className="text-red-600 text-sm" data-testid="login-error">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-black text-white py-3 hover:bg-neutral-800 transition-colors text-sm tracking-wide disabled:opacity-50"
              data-testid="login-submit-button"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
            <p className="text-sm text-neutral-600" data-testid="login-lookup-hint">
              No account? Staff accounts are created by your administrator. To view
              an existing reservation, use the{" "}
              <Link to="/manage-booking" className="link-underline font-semibold">
                booking lookup
              </Link>.
            </p>
          </form>
        </div>

        <div className="lg:col-span-6 hidden lg:block">
          <div className="overflow-hidden" style={{ aspectRatio: "4/5" }}>
            <img
              src="https://customer-assets-39nsmqrw.emergentagent.net/job_auto-reserve-52/artifacts/g9vfrecs_carrentapp-logincars1.webp"
              alt="Car Rental App"
              className="w-full h-full object-contain"
            />
          </div>
        </div>
      </div>
      </div>
      <SiteFooter />
    </div>
  );
}
